"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.safariClient = exports.SafariClient = void 0;
var helpers_1 = require("../helpers");
var index_1 = require("../../index");
/**
 * Enum defining different types of Safari extension events.
 */
var SafariExtensionEventType;
(function (SafariExtensionEventType) {
    SafariExtensionEventType["Monitor"] = "Monitor";
    SafariExtensionEventType["Ping"] = "Ping";
    SafariExtensionEventType["Request"] = "Request";
})(SafariExtensionEventType || (SafariExtensionEventType = {}));
/**
 * Global keep alive timeout to prevent recursion.
 */
var keepAliveTimeout;
/**
 * Handles communication with the Safari extension using JSON-RPC over custom events.
 */
var SafariClient = /** @class */ (function () {
    /**
     * Initializes the SafariExtensionHandler instance.
     * Sets up the promise executor map and starts listening to extension events.
     */
    function SafariClient() {
        var _this = this;
        this.statusInterval = 100;
        this.keepAliveInterval = 1000;
        this.isFirstPing = true;
        this.lastPing = null;
        this.lastPong = null;
        this.safariExtensionEnabled = false;
        this.subscribedTransferActivity = false;
        /**
         * Sends a JSON-RPC request to the Safari extension.
         * @param method The method name to invoke on the extension.
         * @param payload Optional payload for the request.
         * @returns A Promise that resolves with the response from the extension.
         */
        this.request = function (method, payload) {
            if (payload === void 0) { payload = {}; }
            return _this.dispatchPromiseEvent(SafariExtensionEventType.Request, method, payload);
        };
        this.promiseExecutors = new Map();
        this.listenResponseEvents();
        this.listenTransferActivityEvents();
        this.listenStatusEvents();
        this.listenPongEvents();
        if (keepAliveTimeout) {
            clearTimeout(keepAliveTimeout);
        }
        this.keepAlive();
    }
    /**
     * Monitors transfer activity.
     * @returns A Promise that resolves with the response from the extension.
     */
    SafariClient.prototype.monitorTransferActivity = function () {
        var _this = this;
        var dispatchMonitorEvent = function () {
            var promise = _this.dispatchPromiseEvent(SafariExtensionEventType.Monitor, 'subscribe_transfer_activity', [index_1.asperaSdk.globals.appId]);
            return promise.then(function () {
                _this.subscribedTransferActivity = true;
            });
        };
        if (this.safariExtensionEnabled) {
            return dispatchMonitorEvent();
        }
        return new Promise(function (resolve, reject) {
            var extensionInterval = setInterval(function () {
                if (!_this.safariExtensionEnabled) {
                    return;
                }
                dispatchMonitorEvent()
                    .then(resolve)
                    .catch(reject);
                clearInterval(extensionInterval);
            }, 1000);
        });
    };
    /**
     * Builds a JSON-RPC request object with a unique identifier.
     * @param method The method name to invoke on the extension.
     * @param payload Optional parameters for the method.
     * @returns The constructed JSON-RPC request object.
     */
    SafariClient.prototype.buildRPCRequest = function (method, payload) {
        return {
            jsonrpc: '2.0',
            method: method,
            params: payload,
            id: (0, helpers_1.randomUUID)()
        };
    };
    /**
     * Dispatches a custom event to the document to communicate with the Safari extension.
     * @param type The type of Safari extension event to dispatch.
     * @param request Optional JSON-RPC request payload to send with the event.
     */
    SafariClient.prototype.dispatchEvent = function (type, request) {
        var payload = {
            detail: request !== null && request !== void 0 ? request : {}
        };
        document.dispatchEvent(new CustomEvent("AsperaDesktop.".concat(type), payload));
    };
    /**
     * Dispatches a custom event to the document to communicate with the Safari extension.
     * @param type The type of Safari extension event to dispatch.
     * @param method The method name to invoke on the extension.
     * @param payload Optional parameters for the method.
     */
    SafariClient.prototype.dispatchPromiseEvent = function (type, method, payload) {
        var _this = this;
        var request = this.buildRPCRequest(method, payload);
        return new Promise(function (resolve, reject) {
            if (_this.safariExtensionEnabled) {
                _this.promiseExecutors.set(request.id, { resolve: resolve, reject: reject });
                _this.dispatchEvent(type, request);
            }
            else {
                console.warn('The Safari extension is disabled or unresponsive (dispatch event)');
                console.warn("Failed event: ".concat(JSON.stringify(request)));
                reject('The Safari extension is disabled or unresponsive (dispatch event)');
            }
        });
    };
    /**
     * Handles incoming JSON-RPC responses from the Safari extension.
     * Resolves or rejects promises based on the response.
     * @param response The JSON-RPC response object received from the extension.
     */
    SafariClient.prototype.handleResponse = function (response) {
        var requestId = response.id;
        var executor = this.promiseExecutors.get(requestId);
        if (!executor) {
            console.warn("Unable to find a promise executor for ".concat(requestId));
            console.warn("Response: ".concat(response));
            return;
        }
        this.promiseExecutors.delete(requestId);
        if (response.error) {
            executor.reject(response.error);
            return;
        }
        executor.resolve(response.result);
    };
    /**
     * Listens for 'AsperaDesktop.Response' events.
     */
    SafariClient.prototype.listenResponseEvents = function () {
        var _this = this;
        document.addEventListener('AsperaDesktop.Response', function (event) {
            _this.handleResponse(event.detail);
        });
    };
    /**
     * Listens for 'AsperaDesktop.TransferActivity' events.
     */
    SafariClient.prototype.listenTransferActivityEvents = function () {
        document.addEventListener('AsperaDesktop.TransferActivity', function (event) {
            index_1.asperaSdk.activityTracking.handleTransferActivity(event.detail);
        });
    };
    /**
     * Listens for 'AsperaDesktop.Status' events.
     */
    SafariClient.prototype.listenStatusEvents = function () {
        document.addEventListener('AsperaDesktop.Status', function (event) {
            index_1.asperaSdk.activityTracking.handleWebSocketEvents(event.detail);
        });
    };
    /**
     * Listens for 'AsperaDesktop.Pong' events.
     */
    SafariClient.prototype.listenPongEvents = function () {
        var _this = this;
        document.addEventListener('AsperaDesktop.Pong', function () {
            _this.lastPong = Date.now();
            _this.safariExtensionStatusChanged(true);
        });
    };
    /**
     * Sends a keep alive ping according to the defined interval.
     */
    SafariClient.prototype.keepAlive = function () {
        var _this = this;
        this.lastPing = Date.now();
        this.dispatchEvent(SafariExtensionEventType.Ping);
        if (this.isFirstPing) {
            this.isFirstPing = false;
        }
        else {
            setTimeout(function () {
                _this.checkSafariExtensionStatus();
            }, this.statusInterval);
        }
        keepAliveTimeout = setTimeout(function () {
            _this.keepAlive();
        }, this.keepAliveInterval);
    };
    /**
     * Listens for Safari extension status changes.
     * If the extension was disabled and enabled again after initializing the SDK, it
     * will call 'monitorTransferActivity' to resume transfer activities.
     */
    SafariClient.prototype.safariExtensionStatusChanged = function (isEnabled) {
        var _this = this;
        if (isEnabled === this.safariExtensionEnabled) {
            return;
        }
        this.safariExtensionEnabled = isEnabled;
        if (isEnabled) {
            if (this.subscribedTransferActivity) {
                var resumeTransferActivity_1 = function () {
                    _this.monitorTransferActivity()
                        .catch(function () {
                        console.error('Failed to resume transfer activity, will try again in 1s');
                        setTimeout(function () {
                            resumeTransferActivity_1();
                        }, 1000);
                    });
                };
                resumeTransferActivity_1();
            }
        }
        else {
            index_1.asperaSdk.activityTracking.handleWebSocketEvents('CLOSED');
            this.promiseExecutors.forEach(function (promiseExecutor) {
                promiseExecutor.reject('The Safari extension is disabled or unresponsive (extension status)');
            });
            this.promiseExecutors.clear();
        }
        index_1.asperaSdk.activityTracking.handleSafariExtensionEvents(this.safariExtensionEnabled ? 'ENABLED' : 'DISABLED');
    };
    /**
     * Checks if the last pong received was longer than the max interval.
     */
    SafariClient.prototype.checkSafariExtensionStatus = function () {
        var pingPongDiff = this.lastPong - this.lastPing;
        if (this.lastPong == null || pingPongDiff < 0 || pingPongDiff > 500) {
            this.safariExtensionStatusChanged(false);
        }
    };
    return SafariClient;
}());
exports.SafariClient = SafariClient;
exports.safariClient = new SafariClient();
exports.default = {
    safariClient: exports.safariClient
};
